//
// AHT Weight Management — Orlistat (Xenical®) Transfer Supply Pathway
// Source: AHT_Orlistat_Clinical_Pathway.docx v1.0
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// For patients transferring from another provider already established on orlistat.

import type { Pathway } from "../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q0_RED_FLAGS     = "orl-trans-q0-red-flags";
const Q1_F2F           = "orl-trans-q1-f2f";
const CONFIRM_CARD     = "orl-trans-confirmation-card";
const PGD_FRESH_CHECKS = "orl-trans-pgd-fresh-checks";
const Q3_INT_CICLO     = "orl-trans-int1-ciclosporin";
const Q4_INT_WARF      = "orl-trans-int2-warfarin";
const Q5_INT_LEVO      = "orl-trans-int3-levothyroxine";
const Q6_INT_AED       = "orl-trans-int4-antiepileptic";
const Q7_CAUTIONS      = "orl-trans-cautions";
const Q8_TRANSFER_FLAG = "orl-trans-transfer-flag";
const Q9_ATTEST        = "orl-trans-attest";

const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtOrlistatTransferPathway: Pathway = {
  id: "aht-wt-orlistat-transfer",
  name: "Orlistat (Xenical®) — Transfer Supply",
  population: "Adults transferring from another provider already established on orlistat",
  pgdRefs: ["pgd-aht-wt-orlistat"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q0_RED_FLAGS,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 0 — Red flags gateway
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0_RED_FLAGS,
      type: "gateway_progression",
      triggersPayment: false,
      heading: "Check for red flags before proceeding",
      body:
        "Before proceeding with transfer supply, confirm the patient has none of the following:\n" +
        "• Oxalate nephropathy symptoms (flank pain, haematuria) — refer\n" +
        "• Signs of severe hepatic dysfunction\n" +
        "• Worsening seizure control (if on antiepileptics)\n" +
        "• Inadequate INR control (if on warfarin)\n" +
        "• Pregnancy confirmed or suspected — discontinue\n" +
        "• Less than 5% weight loss after 12 weeks — discontinue\n" +
        "If any red flag is present, do not proceed — refer to GP as appropriate.",
      next: Q1_F2F,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedFaceToFace"] === false,
          then: { outcomeId: OUT_NO_F2F },
        },
      ],
      default: "orl-trans-q-pregnancy",
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2a — Pregnancy / breastfeeding check
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: "orl-trans-q-pregnancy",
      type: "yes_no",
      text: "Is the patient pregnant or breastfeeding?",
      hint: "Absolute contraindication in both pregnancy and breastfeeding.",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
      autoAnswer: { when: { patientIsFemale: false }, value: false },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2b — PGD inclusion / exclusion checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: PGD_FRESH_CHECKS,
      type: "pgd_exclusion_check",
      variant: "sequential",
      pgdIdOverride: "pgd-aht-wt-orlistat",
      anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
      allCleared: Q3_INT_CICLO,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3 — Interaction checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q3_INT_CICLO,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["q17Ciclosporin"] === true,
          then: { outcomeId: OUT_CANNOT_SUPPLY },
        },
      ],
      default: Q4_INT_WARF,
    },

    {
      id: Q4_INT_WARF,
      type: "yes_no",
      text: "Is the patient taking warfarin or other oral anticoagulants?",
      hint: "Orlistat may increase anticoagulant effect — monitor INR.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document INR monitoring plan and frequency",
        acknowledgementText: "Patient and GP/anticoagulation clinic have been informed of enhanced INR monitoring",
      },
      yes: Q5_INT_LEVO,
      no: Q5_INT_LEVO,
      autoAnswer: { when: { hasMedication: ["warfarin", "acenocoumarol", "phenindione"] }, value: true },
    },

    {
      id: Q5_INT_LEVO,
      type: "yes_no",
      text: "Is the patient taking levothyroxine?",
      hint: "Reduced absorption — separate administration times; monitor thyroid function.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document levothyroxine administration timing and TSH monitoring plan",
        acknowledgementText: "Patient has been counselled on timing of administration and monitoring requirements",
      },
      yes: Q6_INT_AED,
      no: Q6_INT_AED,
      autoAnswer: { when: { hasMedication: ["levothyroxine"] }, value: true },
    },

    {
      id: Q6_INT_AED,
      type: "yes_no",
      text: "Is the patient taking antiepileptic drugs (valproate or lamotrigine)?",
      hint: "Reduced absorption — monitor seizure frequency and severity.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document seizure monitoring plan and frequency",
        acknowledgementText: "Patient and prescriber have been informed of monitoring requirements for seizure control",
      },
      yes: Q7_CAUTIONS,
      no: Q7_CAUTIONS,
      autoAnswer: { when: { hasMedication: ["valproate", "sodium valproate", "lamotrigine"] }, value: true },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Cautions review
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q7_CAUTIONS,
      type: "flag",
      variant: "warn",
      message:
        "Review cautions before supply:\n" +
        "• High-fat diet substantially increases GI side effects — reinforce dietary guidance before supply\n" +
        "• Limit fat to ~30% of calories (~67g/day on 2,000 kcal diet) distributed across three meals\n" +
        "• CKD or volume depletion: oxalate nephropathy risk — advise adequate hydration\n" +
        "• Levothyroxine: reduced absorption — separate administration; monitor thyroid function\n" +
        "• T2DM on antidiabetics: weight loss may improve glycaemia — antidiabetic dose reduction may be required\n" +
        "• Fat-soluble vitamins (A, D, E, K): absorption reduced — advise multivitamin at bedtime",
      next: Q8_TRANSFER_FLAG,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Transfer notice (no dose select — orlistat has no titration)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q8_TRANSFER_FLAG,
      type: "flag",
      variant: "info",
      message:
        "Transfer from another provider — patient is already established on this medication.\n" +
        "Orlistat 120mg three times daily is the only licensed dose — no dose selection required.\n" +
        "• Request evidence of previous supply (prescription, dispensing label, or transfer letter)\n" +
        "• Advise the patient to inform their GP of the provider transfer",
      next: Q9_ATTEST,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Supply attestation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q9_ATTEST,
      type: "attestation",
      text: "Confirm transfer supply of orlistat 120mg (Xenical®)",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied 84 × orlistat 120mg capsules (28-day supply at 120mg three times daily)\n" +
        "• Verified previous supply against evidence from prior provider\n" +
        "• Reinforced: take immediately before, during, or within 1 hour of each main meal containing fat\n" +
        "• Reinforced: omit dose if meal is missed or contains no fat\n" +
        "• Reinforced: multivitamin supplement at bedtime or ≥2 hours after orlistat\n" +
        "• Advised the patient to inform their GP of the provider transfer\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    [OUT_NO_F2F]: {
      id: OUT_NO_F2F,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — face-to-face consultation required",
      reason: "Supply under this PGD requires the patient to attend in person at AHT registered premises.",
      safetyNetting: ["Ask the patient to book an in-person appointment to proceed with supply."],
    },
    [OUT_CANNOT_SUPPLY]: {
      id: OUT_CANNOT_SUPPLY,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — orlistat 120mg (Xenical®)",
      reason: "An inclusion criterion was not met, an exclusion criterion was triggered, or a contraindicated drug interaction was identified.",
      safetyNetting: [
        "Discuss the reason supply cannot proceed with the patient.",
        "Consider referral to prescriber or GP if clinically appropriate.",
      ],
    },
    [OUT_SUPPLY]: {
      id: OUT_SUPPLY,
      type: "outcome",
      outcomeType: "supply",
      title: "Transfer supply complete — orlistat 120mg",
      reason: "All inclusion criteria met, no exclusion criteria triggered, and transfer supply attestation confirmed.",
      drugRef: "orlistat",
      safetyNetting: [
        "Return in 4 weeks for monthly monitoring review.",
        "Take orlistat immediately before, during, or within 1 hour of each main meal containing fat. Omit if meal is missed or fat-free.",
        "Take a multivitamin daily at bedtime (at least 2 hours after orlistat).",
        "GI side effects are proportional to fat intake — keep fat within limits to minimise symptoms.",
      ],
    },
  },
};
